import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { StarIcon } from '@heroicons/react/24/solid';
import Badge from '../../../shared/TailwindComponents/Badge';
import { useBreadcrumbs } from '../../../../contexts/BreadcrumbsContext';
import { RootState, useAppDispatch } from '../../../../store';
import { useSelector } from 'react-redux';
import { showFeedbackModal } from '../../../../store/actions/UserFeedbackActions';
import Tabs from '../../../shared/TailwindComponents/Tabs';
import { showFileReaderSidePanel } from '../../../../store/actions/sidePanelActions';
import { showSidePanel } from '../../../../store/actions/sidePanelActions';
import apis from '../../../../utils/apis';
import { motion } from 'framer-motion';
import {
    PiBriefcaseLight,
    PiUsersLight,
    PiEnvelopeLight,
    PiPhoneLight,
    PiCalendarLight,
    PiClockLight,
    PiCertificateLight,
    PiBookOpenLight,
    PiTrophyLight,
    PiTranslateLight,
    PiTagLight,
    PiCurrencyCircleDollarLight,
    PiFileTextLight,
    PiCaretLeftLight,
} from 'react-icons/pi';
import Table from '../../../shared/TailwindComponents/Table';
import { LayoutContext } from '../../../../contexts/LayoutContext';
import { useTranslation } from 'react-i18next';
import Button from '../../../shared/TailwindComponents/Button';
import AssignTaskForm from '.././AssignTaskForm';
import { FiLock } from 'react-icons/fi';
import { BadgeColor } from '../../../shared/TailwindComponents/types/badgeTypes';
import ProfileImage from '../../../Header/ProfileImage';
import { fetchExternalCollaboratorDetails } from '../../../../store/actions/organization/ExternalPeopleActions';
import GlassCard from '../../../shared/brand/GlassCard';
import { normalise } from '../../../shared/access/accessModel';
import Skeleton from '../../../shared/TailwindComponents/Skeleton';

function parseToItemsArray(field: any): Array<{ title: string; fileUrl?: string }> {
    if (!field) return [];
    if (Array.isArray(field)) {
        return field.map((item: any, idx: number) => ({
            title: typeof item.title === 'string' ? item.title : `Item (${idx + 1})`,
            fileUrl: typeof item.fileUrl === 'string' ? item.fileUrl : undefined,
        }));
    }
    if (typeof field === 'string') {
        try {
            const parsed = JSON.parse(field);
            if (Array.isArray(parsed)) {
                return parsed.map((item: any, idx: number) => ({
                    title: typeof item.title === 'string' ? item.title : `Item (${idx + 1})`,
                    fileUrl: typeof item.fileUrl === 'string' ? item.fileUrl : undefined,
                }));
            }
        } catch {
            const items = field.split(',').map((x: string) => x.trim()).filter(Boolean);
            return items.map((val, i) => ({ title: val }));
        }
    }
    return [];
}

// Types
interface LawFirmProfile {
    ProfilePicture?: string;
    Designation?: string;
    Bio?: string;
    ExperienceYears?: number;
    HourlyRate?: number;
    LanguagesSpoken?: string;
    Specializations?: string;
    Awards?: any;
    Certifications?: any;
    Publications?: any;
    Department?: string;
    JoinDate?: string;
    Role?: string;
    Status?: string;
}

interface BusinessProfile {
    Designation?: string;
    Status?: string;
    Department?: string;
    JoinDate?: string;
    Role?: string;
    ProfilePicture?: string;
}


interface ExternalCollaborator {
    ExternalID: string;
    Email: string;
    LinkedOrgName?: string;
    AccessLevel: string;
    Status: string;
    CreatedAt: string;
    Name: string;
    Role: string;
    CaseName?: string;
    UserID?: string;
    JoinDate?: string;
    Department?: string;
    FirstName: string;
    LastName: string;
    PhoneNumber?: string;
    is_deleted?: boolean;
    ProfilePicture?: string | null;
    LawFirmProfile?: LawFirmProfile;
    BusinessProfile?: BusinessProfile;
    RatingsAverage?: number;
    RatingsCount?: number;

}

interface EmployeeDetailParams {
    externalId: string;
}

const UpcomingWork: React.FC<{ cases: any[], tasks: any[] }> = ({ cases, tasks }) => {
    const { t } = useTranslation();
    const history = useHistory();

    const renderWorkItem = (item: any, type: 'case' | 'task') => {
        const isTask = type === 'task';
        const title = isTask ? item.Title : item.CaseName;
        const subtitle = isTask ? item.Description : `${t('Client')}: ${item.ClientName}`;
        const badges = isTask
            ? [{ label: item.Status, color: item.Status === 'Active' ? 'blue' : item.Status === 'Completed' ? 'green' : 'red' }]
            : [{ label: item.case_status, color: item.case_status === 'Active' ? 'blue' : item.case_status === 'Completed' ? 'green' : 'red' }];
        const details = isTask
            ? [{ label: t('Deadline'), value: item.TaskDeadline ? new Date(item.TaskDeadline).toLocaleDateString() : t('None') }]
            : [{ label: t('Last Interaction'), value: item.LastInteraction ? new Date(item.LastInteraction).toLocaleDateString() : t('None') }];

        return (
            <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="glass rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer my-1"
                onClick={() => history.push(`/tasks/${item.TaskID}`)}
            >
                <h4 className="text-md font-semibold text-gray-900 dark:text-white">{title}</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">{subtitle}</p>
                <div className="mt-2 flex gap-2">
                    {badges.map((badge, index) => (
                        <Badge key={index} color={badge.color as BadgeColor}>{badge.label}</Badge>
                    ))}
                </div>
                {details.map((detail, index) => (
                    <div key={index} className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        <span>{detail.label}: {detail.value}</span>
                    </div>
                ))}
            </motion.div>
        );
    };

    return (
        <div className="mt-4 max-h-[calc(100vh-300px)] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4 text-gray-800 dark:text-gray-100">{t('Upcoming Work')}</h3>
            <div className="space-y-4">
                <div>
                    <h4 className="text-lg font-semibold mb-2 text-gray-700 dark:text-gray-200">{t('Tasks')}</h4>
                    {tasks.length > 0 ? tasks.map(task => renderWorkItem(task, 'task')) : (
                        <Badge color="gray">{t('No upcoming tasks')}</Badge>
                    )}
                </div>
            </div>
        </div>
    );
};

const EmployeeDetail: React.FC = () => {
    const { t } = useTranslation();
    const { externalId } = useParams<EmployeeDetailParams>();

    const dispatch = useAppDispatch();
    const { setBreadcrumbs } = useBreadcrumbs();
    const employeesState = useSelector((state: RootState) => state.employees);
    const { error } = employeesState;

    const [employee, setEmployee] = useState<ExternalCollaborator | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<'info' | 'tasks' | 'cases' | 'timekeeping'>('info');
    const [tasks, setTasks] = useState<any[]>([]);
    const [tasksLoading, setTasksLoading] = useState<boolean>(false);
    const [tasksError, setTasksError] = useState<string>('');
    const [cases, setCases] = useState<any[]>([]);
    const [casesLoading, setCasesLoading] = useState<boolean>(false);
    const [casesError, setCasesError] = useState<string>('');
    const [tasksPage, setTasksPage] = useState(1);
    const [tasksHasMore, setTasksHasMore] = useState(true);
    const [casesPage, setCasesPage] = useState(1);
    const [casesHasMore, setCasesHasMore] = useState(true);
    const [upcomingWork, setUpcomingWork] = useState<{ cases: any[], tasks: any[] } | null>(null);
    const [workLoading, setWorkLoading] = useState<boolean>(false);
    const [workError, setWorkError] = useState<string>('');
    const { setIsContentScrollable } = useContext(LayoutContext);
    const [showTaskForm, setShowTaskForm] = useState(false);
    const [previousTab, setPreviousTab] = useState(activeTab);
    const { subscriptionActive } = useSelector((state: RootState) => state.subscription);
    const history = useHistory();

    useEffect(() => {
        setIsContentScrollable(false);
        return () => setIsContentScrollable(true);
    }, [setIsContentScrollable]);

    useEffect(() => {
        if (activeTab === 'cases') {
            fetchEmployeeCases(casesPage)

        }
    }, [activeTab])


    const loadDetails = useCallback(async () => {
        setLoading(true);
        try {
            let details = await dispatch(fetchExternalCollaboratorDetails(externalId))
            if (details) setEmployee(details);
            else
                dispatch(
                    showFeedbackModal({
                        showModal: true,
                        message: t('Could not load employee details.'),
                        modalType: 'error',
                        duration: 3000,
                    })
                );


        } finally {
            setLoading(false);
        }
    }, [dispatch, externalId, t]);

    useEffect(() => {
        loadDetails();
    }, [loadDetails]);

    useEffect(() => {
        if (employee && employee.Email) {
            setWorkLoading(true);
            apis.post('/user/get_upcoming_work', { UserEmail: employee.Email })
                .then(response => {
                    if (response.status === 200) {
                        setUpcomingWork({
                            cases: response.data.upcoming_cases.slice(0, 5),
                            tasks: response.data.upcoming_tasks.slice(0, 5)
                        });
                    } else {
                        setWorkError(t('Could not fetch upcoming work.'));
                    }
                })
                .catch(() => setWorkError(t('Failed to fetch upcoming work.')))
                .finally(() => setWorkLoading(false));
        }
    }, [employee, t]);

    useEffect(() => {
        if (!employee || !employee.UserID) return;
        if (activeTab === 'tasks' && tasks.length === 0) {
            setTasks([]);
            setTasksPage(1);
            setTasksHasMore(true);
            fetchEmployeeTasks(1);
        } else if (activeTab === 'cases' && cases.length === 0) {
            setCases([]);
            setCasesPage(1);
            setCasesHasMore(true);
            fetchEmployeeCases(1);
        }
    }, [activeTab, employee]);

    const fetchEmployeeTasks = async (page: number) => {
        setTasksLoading(true);
        try {
            const resp = await apis.post('/tasks/get_assigned_tasks', {
                UserEmail: employee?.Email,
                page,
                limit: 10,
            });
            if (resp?.status === 200) {
                const newTasks = resp.data.Tasks;
                setTasks((prev) => [...prev, ...newTasks]);
                setTasksHasMore(page * 10 < resp.data.total);
            } else {
                setTasksError(t('Could not fetch tasks.'));
            }
        } catch (err) {
            console.error('Error fetching tasks', err);
            setTasksError(t('Failed to fetch tasks.'));
        } finally {
            setTasksLoading(false);
        }
    };

    const fetchEmployeeCases = async (page: number) => {
        setCasesLoading(true);
        try {
            const resp = await apis.post('/cases/get_assigned_cases', {
                UserEmail: employee?.Email,
                page,
                limit: 10,
            });
            if (resp?.status === 200) {
                const newCases = resp.data.cases;
                setCases(newCases)
                setCasesHasMore(page * 10 < resp.data.total);
            } else {
                setCasesError(t('Could not fetch cases.'));
            }
        } catch (err) {
            console.error('Error fetching cases', err);
            setCasesError(t('Failed to fetch cases.'));
        } finally {
            setCasesLoading(false);
        }
    };
    const loadMoreMatters = async (page: number) => {
        setCasesLoading(true);
        try {
            const resp = await apis.post('/cases/get_assigned_cases', {
                UserEmail: employee?.Email,
                page,
                limit: 10,
            });
            if (resp?.status === 200) {
                const newCases = resp.data.cases;
                setCases(newCases)
                setCasesHasMore(page * 10 < resp.data.total);
            } else {
                setCasesError(t('Could not fetch cases.'));
            }
        } catch (err) {
            console.error('Error fetching matters', err);
            setCasesError(t('Failed to fetch matters.'));
        } finally {
            setCasesLoading(false);
        }
    };
    const loadMoreTasks = async (page: number) => {
        setTasksLoading(true);
        try {
           
            setTasksPage((prev) => prev + 1);
            fetchEmployeeTasks(tasksPage + 1);
        } catch (err) {
            console.error('Error fetching tasks', err);
            setCasesError(t('Failed to fetch tasks.'));
        } finally {
            setTasksLoading(false);
        }
    };

    useEffect(() => {
        if (employee) {
            const empName = `${employee.FirstName || t('Unknown')} ${employee.LastName || t('Employee')}`;
            setBreadcrumbs([
                { name: t('People'), href: '/people', current: false },
                { name: empName, href: `/people/${externalId}`, current: true },
            ]);
        } else {
            setBreadcrumbs([
                { name: t('People'), href: '/people', current: false },
                { name: t('Loading'), href: `/people/${externalId}`, current: true },
            ]);
        }
        return () => setBreadcrumbs([]);
    }, [employee, externalId, setBreadcrumbs, t]);

    if (loading) return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="p-6"
        >
            <Skeleton variant="table" rows={6} />
        </motion.div>
    );

    if (error || !employee) {
        return <div className="text-danger-600 p-4">{error || t('Employee not found.')}</div>;
    }

    const employeeStatus = employee.is_deleted ? t('Inactive') : t('Active');
    const ratingStars = [0, 1, 2, 3, 4];
    const avgRating = employee.RatingsAverage || 0;
    const totalReviews = employee.RatingsCount || 0;
    const isLawyer = !!employee.LawFirmProfile && !employee.BusinessProfile;
    const profile = isLawyer ? employee.LawFirmProfile || {} : employee.BusinessProfile || {};
    const {
        Bio,
        LanguagesSpoken,
        Specializations,
        Awards,
        Certifications,
        Publications,
    } = employee.LawFirmProfile || {};

    const specializationsArr = Specializations ? Specializations.split(',').map((s) => s.trim()) : [];
    const languagesArr = LanguagesSpoken ? LanguagesSpoken.split(',').map((l) => l.trim()) : [];
    const awardsList = parseToItemsArray(Awards);
    const certsList = parseToItemsArray(Certifications);
    const publicationsList = parseToItemsArray(Publications);

    const handleOpenFile = (fileUrl?: string) => {
        if (fileUrl) dispatch(showFileReaderSidePanel(fileUrl, ''));
    };

    const handleOpenTaskDetail = (taskObj: any) => {
        dispatch(
            showSidePanel({
                type: 'employeeTaskDetail',
                showSidePanel: true,
                size: 'md',
                taskData: taskObj,
            })
        );
    };



    const handleOpenCaseDetail = (caseObj: any) => {
        dispatch(
            showSidePanel({
                type: 'employeeCaseDetail',
                showSidePanel: true,
                size: 'md',
                caseData: caseObj,
            })
        );
    };

    const handleShowTaskForm = () => {
        setPreviousTab(activeTab);
        setShowTaskForm(true);
    };

    const handleTaskCreated = () => {
        setShowTaskForm(false);
        setActiveTab(previousTab);
        fetchEmployeeTasks(1);
    };

    const tabs = [
        { label: 'Info', value: 'info' },
        { label: 'Tasks', value: 'tasks' },
        { label: 'Cases', value: 'cases' },
    ];

    const renderTasksTab = () => {
        if (tasksError) return <div className="text-danger-600 p-4 text-center">{tasksError}</div>;
        if (tasks.length === 0 && !tasksHasMore && !tasksLoading)
            return (
                <div className="flex justify-center mt-5">
                    <Badge color="gray">{t('No tasks available.')}</Badge>
                </div>
            );

        const taskColumns = ['Task Name', 'Status', 'Due Date'];
        const taskData = tasks.map((task) => ({
            id: task.TaskID || task.id,
            'Task Name': task.Title || t('None'),
            'Status': t(task.Status) || t('None'),
            'Due Date': task.TaskDeadline ? new Date(task.TaskDeadline).toLocaleDateString() : t('None'),
        }));

        return (
            <div className="overflow-y-hidden max-h-[calc(100vh-120px)]">
                <div className="mr-4 flex justify-end">
                    <Button
                        variant="primary"
                        size="small"
                        onClick={handleShowTaskForm}
                        className="relative"
                        disabled={!subscriptionActive}
                    >
                        {t("Assign Task")}
                        {!subscriptionActive && (
                            <FiLock
                                className="absolute -top-2 -right-2 text-warning-500 bg-white dark:bg-gray-800 rounded-full shadow-md p-[2px]"
                                size={16}
                                title={t('Premium Feature')}
                            />
                        )}
                    </Button>
                </div>
                    <Table
                        columns={taskColumns}
                        data={taskData}
                        max='max-h-[calc(100vh-330px)]'
                        min='max-h-[calc(100vh-330px)]'
                        onRowClick={(row) => handleOpenTaskDetail(tasks.find((t) => t.TaskID === row.id || t.id === row.id))}
                        hasMore={tasksHasMore}
                        isLoadingMore={tasksLoading}
                        onLoadMore={() => loadMoreTasks(casesPage + 1)}
                    />
            </div>
        );
    };



    const renderCasesTab = () => {

        if (casesError) return <div className="text-danger-600 p-4 text-center">{casesError}</div>;
        if (cases.length === 0 && !casesHasMore && !casesLoading)
            return (
                <div className="flex justify-center mt-5">
                    <Badge color="gray">{t('No cases available.')}</Badge>
                </div>
            );

        const caseColumns = ['Case Name', 'Client', 'Status', 'Created'];
        const caseData = cases.map((caseItem) => ({
            id: caseItem.CaseID || caseItem.id,
            'Case Name': caseItem.CaseName || `${t('Case')} ${caseItem.CaseID}`,
            'Client': caseItem.ClientName || t('None'),
            'Status': caseItem.case_status || t('None'),
            'Created': caseItem.created_at ? new Date(caseItem.created_at).toLocaleDateString() : t('None'),
        }));

        return (
            <div className="overflow-y-hidden">
                <Table
                    columns={caseColumns}
                    data={caseData}
                    max='max-h-[calc(100vh-300px)]'
                    min='max-h-[calc(100vh-300px)]'
                    onRowClick={(row) => handleOpenCaseDetail(cases.find((c) => c.CaseID === row.id || c.id === row.id))}
                    hasMore={casesHasMore}
                    isLoadingMore={casesLoading}
                    onLoadMore={() => loadMoreMatters(casesPage + 1)}
                />
            </div>
        );
    };

    const renderInfoTab = () => {
        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="flex flex-col md:flex-row gap-6"
            >
                <div className="flex-1 mt-2 transition-opacity duration-300">
                    <GlassCard icon={<PiFileTextLight />} title={t('About')} className="mt-2">
                        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                            {Bio ? Bio : t('No information available.')}
                        </p>
                    </GlassCard>

                    <div className="mt-6">
                        <div className="flex items-center space-x-2 mb-2">
                            <PiTagLight className="text-gray-500 w-5 h-5" />
                            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">
                                {t('Specializations')}
                            </h3>
                        </div>
                        {specializationsArr.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                                {specializationsArr.map((spec) => (
                                    <span key={spec} className="inline-flex items-center px-2 py-1 text-sm font-medium text-gray-700 bg-gray-100 dark:bg-gray-700 dark:text-gray-300 rounded">
                                        {spec}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <Badge color="gray">{t('No specializations available.')}</Badge>
                        )}
                    </div>

                    <div className="mt-6">
                        <div className="flex items-center space-x-2 mb-2">
                            <PiTranslateLight className="text-gray-500 w-5 h-5" />
                            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">
                                {t('Languages Spoken')}
                            </h3>
                        </div>
                        {languagesArr.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                                {languagesArr.map((lang) => (
                                    <span key={lang} className="inline-flex items-center px-2 py-1 text-sm font-medium text-gray-700 bg-gray-100 dark:bg-gray-700 dark:text-gray-300 rounded">
                                        {lang}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <Badge color="gray">{t('No languages specified.')}</Badge>
                        )}
                    </div>

                    <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                            <div className="flex items-center space-x-2 mb-2">
                                <PiTrophyLight className="text-gray-500 w-5 h-5" />
                                <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-300">
                                    {t('Awards')}
                                </h4>
                            </div>
                            {awardsList.length > 0 ? (
                                <ul className="space-y-1">
                                    {awardsList.map((awardItem, idx) => (
                                        <li key={idx} className="text-sm text-gray-700 dark:text-gray-300">
                                            {awardItem.fileUrl ? (
                                                <button
                                                    type="button"
                                                    onClick={() => handleOpenFile(awardItem.fileUrl)}
                                                    className="text-info-600 hover:underline transition-colors duration-200"
                                                >
                                                    {awardItem.title || t('Untitled')}
                                                </button>
                                            ) : (
                                                <span>{awardItem.title || t('Untitled')}</span>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <Badge color="gray">{t('No awards received.')}</Badge>
                            )}
                        </div>

                        <div>
                            <div className="flex items-center space-x-2 mb-2">
                                <PiCertificateLight className="text-gray-500 w-5 h-5" />
                                <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-300">
                                    {t('Certifications')}
                                </h4>
                            </div>
                            {certsList.length > 0 ? (
                                <ul className="space-y-1">
                                    {certsList.map((cert, idx) => (
                                        <li key={idx} className="text-sm text-gray-700 dark:text-gray-300">
                                            {cert.fileUrl ? (
                                                <button
                                                    type="button"
                                                    onClick={() => handleOpenFile(cert.fileUrl)}
                                                    className="text-info-600 hover:underline transition-colors duration-200"
                                                >
                                                    {cert.title || t('Untitled')}
                                                </button>
                                            ) : (
                                                <span>{cert.title || t('Untitled')}</span>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <Badge color="gray">{t('No certifications available.')}</Badge>
                            )}
                        </div>

                        <div>
                            <div className="flex items-center space-x-2 mb-2">
                                <PiBookOpenLight className="text-gray-500 w-5 h-5" />
                                <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-300">
                                    {t('Publications')}
                                </h4>
                            </div>
                            {publicationsList.length > 0 ? (
                                <ul className="space-y-1">
                                    {publicationsList.map((pub, idx) => (
                                        <li key={idx} className="text-sm text-gray-700 dark:text-gray-300">
                                            {pub.fileUrl ? (
                                                <a
                                                    href={pub.fileUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-info-600 hover:underline transition-colors duration-200"
                                                >
                                                    {pub.title || t('Untitled')}
                                                </a>
                                            ) : (
                                                <span>{pub.title || t('Untitled')}</span>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <Badge color="gray">{t('No publications available.')}</Badge>
                            )}
                        </div>
                    </div>
                </div>
                <div className="w-full md:w-1/3">
                    {workLoading ? (
                        <Skeleton variant="table" rows={6} />
                    ) : workError ? (
                        <div className="text-danger-600">{workError}</div>
                    ) : upcomingWork ? (
                        <UpcomingWork cases={upcomingWork.cases} tasks={upcomingWork.tasks} />
                    ) : null}
                </div>
            </motion.div>
        );
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'info':
                return renderInfoTab();
            case 'tasks':
                return renderTasksTab();
            case 'cases':
                return renderCasesTab();
            case 'timekeeping':
                return (
                    <div className="flex justify-center mt-5">
                        <Badge color="gray">{t('Coming soon.')}</Badge>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="p-6 transition-all duration-300"
        >
            <button
                onClick={() => history.push('/people')}
                className="text-secondary-900 hover:text-secondary-600 transition-colors mr-4"
            >
                <PiCaretLeftLight className="h-4 w-4" />
            </button>
            <div className="flex flex-col md:flex-row md:items-center md:space-x-6">
                <div className="flex-shrink-0 mb-4 md:mb-0">
                    <ProfileImage profilePicture={normalise(employee).avatar} email={employee.Email || ''} initials={(employee.FirstName?.[0] || 'U') + (employee.LastName?.[0] || 'E')} className='w-32 h-32 md:w-40 md:h-40 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700' />
                </div>
                <div className="flex-1">
                    <div className="flex items-center justify-between">
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-gray-100 transition-colors duration-200">
                            {employee.FirstName || t('Unknown')} {employee.LastName || t('Employee')}
                        </h2>
                        <span
                            className={`text-sm font-medium px-2 py-1 rounded capitalize  ${employeeStatus === 'Active' ? 'bg-success-100 text-success-800' : 'bg-gray-100 text-gray-800'
                                }`}
                        >
                            {t(employeeStatus)}
                        </span>
                    </div>
                    <div className="mt-3 flex items-center space-x-2">
                        {ratingStars.map((_, idx) => (
                            <StarIcon
                                key={idx}
                                className={avgRating > idx ? 'text-warning-400 h-5 w-5' : 'text-gray-300 h-5 w-5'}
                            />
                        ))}
                        <p className="text-gray-600 dark:text-gray-300 text-sm">
                            {avgRating.toFixed(1)} / 5.0 ({totalReviews} {t('Reviews')})
                        </p>
                    </div>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {employee.Email && (
                            <div className="flex items-center space-x-2">
                                <PiEnvelopeLight className="text-gray-500 w-5 h-5" />
                                <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                                    {employee.Email || t('None')}
                                </span>
                            </div>
                        )}
                        {employee.PhoneNumber && (
                            <div className="flex items-center space-x-2">
                                <PiPhoneLight className="text-gray-500 w-5 h-5" />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    {employee.PhoneNumber || t('None')}
                                </span>
                            </div>
                        )}
                        {isLawyer && (profile as LawFirmProfile).ExperienceYears !== undefined && (
                            <div className="flex items-center space-x-2">
                                <PiClockLight className="text-gray-500 w-5 h-5" />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    {(profile as LawFirmProfile).ExperienceYears !== null
                                        ? `${(profile as LawFirmProfile).ExperienceYears} ${t('years')}`
                                        : t('0')}
                                </span>
                            </div>
                        )}
                        {isLawyer && (profile as LawFirmProfile).HourlyRate !== undefined && (
                            <div className="flex items-center space-x-2">
                                <PiCurrencyCircleDollarLight className="text-gray-500 w-5 h-5" />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    {(profile as LawFirmProfile).HourlyRate !== null
                                        ? `$${(profile as LawFirmProfile).HourlyRate!.toFixed(2)}/${t('hour')}`
                                        : t('0')}
                                </span>
                            </div>
                        )}
                        {profile.Role && (
                            <div className="flex items-center space-x-2">
                                <PiBriefcaseLight className="text-gray-500 w-5 h-5" />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    {profile.Role || t('None')}
                                </span>
                            </div>
                        )}
                        {profile.JoinDate && (
                            <div className="flex items-center space-x-2">
                                <PiCalendarLight className="text-gray-500 w-5 h-5" />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    {profile.JoinDate ? new Date(profile.JoinDate).toLocaleDateString() : t('None')}
                                </span>
                            </div>
                        )}
                        {profile.Department && (
                            <div className="flex items-center space-x-2">
                                <PiUsersLight className="text-gray-500 w-5 h-5" />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    {profile.Department || t('None')}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="mt-4">
                {showTaskForm ? (
                    <AssignTaskForm
                        employeeEmail={employee.Email as string}
                        onCancel={() => setShowTaskForm(false)}
                        onAdd={handleTaskCreated}
                    />
                ) : (
                    <>
                        <Tabs tabs={tabs} activeTab={activeTab} onTabClick={(val) => setActiveTab(val as any)} />
                        <div className="mt-4 overflow-y-auto">{renderTabContent()}</div>
                    </>
                )}
            </div>
        </motion.div>
    );
};

export default EmployeeDetail;